import {
  MetaSmall,
  MetaSmallGray,
  ShopifySmall,
  ShopifySmallGray,
  TikTokSmallCircle,
  TikTokSmallGreyCircle
} from '@innovationdepartment/proxima-ui';
import type { ReactNode } from 'react';
import {
  type BrandListIntegrationStatusProps,
  IntegrationListStatus,
  IntegrationStatus,
  IntegrationType
} from 'types/integrations';
import KlaviyoIcon from './Layout/KlaviyoIcon';

const icons: { [key in IntegrationType]: [ReactNode, ReactNode] } = {
  shopify: [<ShopifySmall key="shopify" />, <ShopifySmallGray key="shopify-gray" />],
  facebook: [<MetaSmall key="facebook" />, <MetaSmallGray key="facebook-gray" />],
  tiktok: [<TikTokSmallCircle key="tiktok" />, <TikTokSmallGreyCircle key="tiktok-gray" />],
  klaviyo: [<KlaviyoIcon key="klaviyo" active />, <KlaviyoIcon key="klaviyo-gray" />]
};

function useBrandListIntegrationStatus(props: BrandListIntegrationStatusProps) {
  const { integrations, type, isAdAccountActive } = props;

  const isShopify = type === IntegrationType.Shopify;
  const isFacebook = type === IntegrationType.Facebook;
  const isTiktok = type === IntegrationType.TikTok;
  const isKlaviyo = type === IntegrationType.Klaviyo;

  const integrationsOfType = integrations[type] ?? [];

  let status: IntegrationListStatus = IntegrationListStatus.NonStarted;
  let tooltipContent: React.ReactNode = 'Not yet connected';
  const isConnected = integrationsOfType.some(i => i.status === IntegrationStatus.Active);
  const isDisconnected = !isConnected && integrationsOfType.length > 0;

  if (isShopify || isKlaviyo) {
    const setToConnectedStatus = isConnected;
    const setToDisconnectedStatus = isDisconnected;

    if (setToConnectedStatus) {
      status = IntegrationListStatus.Connected;
      tooltipContent = 'Connected';
    }

    if (setToDisconnectedStatus) {
      status = IntegrationListStatus.Disconnected;
      tooltipContent = 'Disconnected';
    }
  }

  if (isFacebook || isTiktok) {
    const setToCompleteStatus = isConnected && isAdAccountActive;
    const setToIncompleteStatus = isConnected;
    const setToDisconnectedStatus = isDisconnected;

    if (setToIncompleteStatus) {
      status = IntegrationListStatus.Incomplete;
      tooltipContent = 'Incomplete';
    }
    if (setToCompleteStatus) {
      status = IntegrationListStatus.Connected;
      tooltipContent = 'Connected';
    }
    if (setToDisconnectedStatus) {
      status = IntegrationListStatus.Disconnected;
      if (isFacebook) {
        // figure the latest integration and its status
        const latestIntegration = integrationsOfType.reduce((latest, current) => {
          const currentTimestamp = new Date(current.lastUpdateAt).getTime();
          const latestTimestamp = latest ? new Date(latest.lastUpdateAt).getTime() : 0;
          return !latest || currentTimestamp > latestTimestamp ? current : latest;
        });

        const isExpiredOrUnauthorized =
          latestIntegration &&
          (latestIntegration.status === IntegrationStatus.Expired ||
            latestIntegration.status === IntegrationStatus.Unauthorized);
        const message = isExpiredOrUnauthorized ? (latestIntegration?.message ?? '') : '';
        tooltipContent = message.length > 0 ? message : 'Disconnected';
      } else {
        tooltipContent = 'Disconnected';
      }
    }
  }

  const [iconConnected, iconDisconnected] = icons[type];

  const icon = isConnected ? iconConnected : iconDisconnected;

  return {
    icon,
    status,
    tooltipContent
  };
}

export default useBrandListIntegrationStatus;
